import { api } from '../api';

// Peel ApiResponse<PageResponse<T>> down to a plain array for callers that
// don't care about pagination metadata (LeadDetail timeline etc.).
const toArray = (body) =>
  body?.data?.content   // ApiResponse<PageResponse<T>>
  ?? body?.content      // raw PageResponse<T>
  ?? (Array.isArray(body?.data) ? body.data : null)
  ?? (Array.isArray(body) ? body : []);

export default {
  list:       (params)    => api.get('/v1/leads', { params }).then(r => r.data),
  get:        (id)        => api.get(`/v1/leads/${id}`).then(r => r.data),

  /**
   * Create a lead manually (walk-in, phone enquiry).
   * POST /v1/leads — body is a LeadCreationRequest. The server requires at
   * least one of customerPhoneE164 / customerEmail (LeadCreationRequest#hasContact).
   */
  create:     (body)      => api.post('/v1/leads', body).then(r => r.data?.data ?? r.data),
  /**
   * Same POST, but tells the caller whether this was a DUPLICATE (DEF-136):
   * the server returns 201 for a genuinely new lead and 200 when the customer
   * already had an open lead (and `lead` is that existing one). Lets the New
   * Lead modal warn and open the existing lead instead of claiming a new one.
   */
  createDetailed: (body) => api.post('/v1/leads', body).then(r => ({
    lead: r.data?.data ?? r.data,
    duplicate: r.status === 200,
  })),
  // Lookup by customer is performed against the same /v1/leads list
  // endpoint (the controller accepts a `customerId` filter param). If
  // the backend grows a dedicated path we'll switch this caller in one
  // place without touching the page.
  byCustomer: (customerId) =>
    api.get('/v1/leads', { params: { customerId, page: 0, size: 50 } })
       .then(r => toArray(r.data)),
  /**
   * Move a lead to a new stage.
   *
   * Backend contract (LeadController#advanceStage):
   *   PATCH /v1/leads/{id}/stage?to=<LeadStage>
   *   body (optional): { lostReasonCode, lostNotes } — REQUIRED when to=LOST
   *
   * `to` is a @RequestParam, not a body field. Sending it in the body is
   * what produced MissingServletRequestParameterException: 'to'.
   *
   * @param {string} id     lead uuid
   * @param {string} to     a LeadStage enum value (INBOUND … LOST)
   * @param {object} [body] { lostReasonCode, lostNotes }
   */
  updateStage: (id, to, body) =>
    api.patch(`/v1/leads/${id}/stage`, body ?? {}, { params: { to } })
       .then(r => r.data?.data ?? r.data),

  reopen: (id, toStage) =>
    api.post(`/v1/leads/${id}/reopen`, toStage ? { toStage } : {}).then(r => r.data?.data ?? r.data),

  reassign: (id, consultantId) =>
    api.post(`/v1/leads/${id}/reassign`, { consultantId }).then(r => r.data?.data ?? r.data),
  unassign: (id) =>
    api.post(`/v1/leads/${id}/unassign`).then(r => r.data?.data ?? r.data),
  autoAssign: (id) =>
    api.post(`/v1/leads/${id}/auto-assign`).then(r => r.data?.data ?? r.data),

  /** Bulk import leads from a CSV/XLSX file (multipart). */
  importFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/v1/leads/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => r.data?.data ?? r.data);
  },

  scoreHistory: (id) => api.get(`/v1/leads/${id}/score-history`).then(r => toArray(r.data)),
  updateDiscovery: (id, flags) => api.patch(`/v1/leads/${id}/discovery`, flags).then(r => r.data),
  captureTradeIn:  (id, payload) => api.post(`/v1/leads/${id}/trade-in`, payload).then(r => r.data),
  interactions:    (id) => api.get(`/v1/leads/${id}/interactions`).then(r => toArray(r.data)),

  // Takeover / hand-back to AI
  takeover:        (id) => api.post(`/v1/leads/${id}/takeover`).then(r => r.data?.data ?? r.data),
  handBackToAI:    (id) => api.post(`/v1/leads/${id}/hand-back-to-ai`).then(r => r.data?.data ?? r.data),

  // Vehicle of interest — free-text make/model and an optional link to
  // an inventory row.
  /**
   * Reclassify New / Used / Demo. (E4 / T034.)
   * Separate from updateVehicleInterest on purpose — correcting a model name
   * and reclassifying the deal are different acts and should not look alike.
   */
  updateVehicleCategory: (id, category) =>
    api.patch(`/v1/leads/${id}/vehicle-category`, { category }).then(r => r.data?.data ?? r.data),

  updateVehicleInterest: (id, body) =>
    api.patch(`/v1/leads/${id}/vehicle-interest`, body).then(r => r.data?.data ?? r.data),

  /* ── next event (E11) ──
   * The earliest open task per lead. Served by NextEventController; a 404
   * means the backend is not deployed yet and callers should treat every lead
   * as "unknown" rather than "no next step".
   */
  nextEvent:  (id)  => api.get(`/v1/leads/${id}/next-event`).then(r => r.data?.data ?? r.data),
  nextEvents: (ids) => api.get('/v1/leads/next-events', { params: { ids: (ids || []).join(',') } })
                          .then(r => r.data?.data ?? r.data),
};
